import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import Collapse from "@material-ui/core/Collapse";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: theme.spacing(1),
  },
  balance: {
    fontWeight: 700,
  },
  topupRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
}));

// valores = chaves de tradução (texto resolvido no render)
const CATEGORY_LABELS = {
  MARKETING: "templateCredit.categories.marketing",
  UTILITY: "templateCredit.categories.utility",
  AUTHENTICATION: "templateCredit.categories.authentication",
};

const TX_TYPES = {
  purchase: { labelKey: "templateCredit.txTypes.purchase", color: "#4caf50" },
  manual_adjust: { labelKey: "templateCredit.txTypes.manualAdjust", color: "#2196f3" },
  debit: { labelKey: "templateCredit.txTypes.debit", color: "#f44336" },
  refund: { labelKey: "templateCredit.txTypes.refund", color: "#ff9800" },
};

const PRESETS = [25, 50, 100];

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });

// Card de créditos de template (API Oficial). Só aparece para empresas com a
// cobrança habilitada pelo super admin — as demais não veem nada.
export default function TemplateCreditCard({ onPay, refreshKey }) {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);

  const [status, setStatus] = useState(null);
  const [customValue, setCustomValue] = useState("");
  const [creating, setCreating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/template-credits/status");
      setStatus(data);
    } catch (err) {
      // silencioso: card some quando o status não pode ser lido
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, refreshKey]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return undefined;
    const socket = socketManager.getSocket(companyId);

    const onTemplateCredit = (data) => {
      if (data.action === "balance") {
        setStatus((prev) =>
          prev ? { ...prev, balance: data.balance } : prev
        );
      }
    };

    socket.on(`company-${companyId}-templateCredit`, onTemplateCredit);
    return () => {
      socket.off(`company-${companyId}-templateCredit`, onTemplateCredit);
    };
  }, [socketManager]);

  const handleTopup = async (value) => {
    setCreating(true);
    try {
      const { data: invoice } = await api.post("/template-credits/topup", {
        value,
      });
      if (onPay) onPay(invoice);
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  const toggleHistory = async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) {
      setHistoryLoading(true);
      try {
        const { data } = await api.get("/template-credits/transactions");
        setHistoryRows(data.transactions || []);
      } catch (err) {
        toastError(err);
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  if (!status?.enabled) return null;

  const pricedCategories = Object.keys(CATEGORY_LABELS).filter(
    (c) => Number(status.rates?.[c]) > 0
  );

  return (
    <Paper className={classes.card} variant="outlined">
      <Box className={classes.header}>
        <WhatsAppIcon style={{ color: "#25d366" }} />
        <Typography variant="subtitle1">
          {i18n.t("templateCredit.title")}
        </Typography>
      </Box>

      <Typography variant="h5" className={classes.balance}>
        {formatCurrency(status.balance)}
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block">
        {i18n.t("templateCredit.ratesIntro")}{" "}
        {pricedCategories.length > 0
          ? pricedCategories
              .map(
                (c) =>
                  `${i18n.t(CATEGORY_LABELS[c])} ${formatCurrency(status.rates[c])}`
              )
              .join(" · ")
          : i18n.t("templateCredit.ratesUndefined")}
      </Typography>

      <Box className={classes.topupRow}>
        {PRESETS.map((preset) => (
          <Button
            key={preset}
            size="small"
            variant="outlined"
            color="primary"
            disabled={creating}
            onClick={() => handleTopup(preset)}
          >
            + R$ {preset}
          </Button>
        ))}
        <TextField
          size="small"
          variant="outlined"
          type="number"
          label={i18n.t("templateCredit.otherValue")}
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          inputProps={{ min: 1, step: 1 }}
          style={{ width: 130 }}
        />
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={creating || !Number(customValue)}
          onClick={() => handleTopup(Number(customValue))}
        >
          {creating
            ? i18n.t("templateCredit.generating")
            : i18n.t("templateCredit.buy")}
        </Button>
        <Button size="small" onClick={toggleHistory}>
          {historyOpen
            ? i18n.t("templateCredit.hideStatement")
            : i18n.t("templateCredit.showStatement")}
        </Button>
      </Box>

      <Collapse in={historyOpen}>
        <Box mt={2} className={classes.tableWrapper}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("common.date")}</TableCell>
                  <TableCell>{i18n.t("common.type")}</TableCell>
                  <TableCell>{i18n.t("templateCredit.table.template")}</TableCell>
                  <TableCell align="right">
                    {i18n.t("templateCredit.table.value")}
                  </TableCell>
                  <TableCell align="right">
                    {i18n.t("templateCredit.table.balanceAfter")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.map((tx) => {
                  const info = TX_TYPES[tx.type] || {
                    label: tx.type,
                    color: "#9e9e9e",
                  };
                  return (
                    <TableRow key={tx.id}>
                      <TableCell>
                        {moment(tx.createdAt).format("DD/MM/YY HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            info.labelKey ? i18n.t(info.labelKey) : info.label
                          }
                          style={{
                            backgroundColor: info.color,
                            color: "#fff",
                          }}
                        />
                      </TableCell>
                      <TableCell>{tx.templateName || "-"}</TableCell>
                      <TableCell
                        align="right"
                        style={{
                          color:
                            Number(tx.amount) < 0 ? "#f44336" : "#4caf50",
                        }}
                      >
                        {formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(tx.balanceAfter)}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {historyRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("templateCredit.noMovements")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
